import styled, { css } from "styled-components";
import type { FlattenSimpleInterpolation } from "styled-components";

interface Props {
  readonly baseColor: string;
  readonly children?: React.ReactNode;
  readonly className?: string;
}

interface StripedBackgroundOptions {
  readonly baseColor: string;
  readonly stripeOpacity?: number;
  readonly stripeWidth?: number;
}

/**
 * Reusable diagonal-hatch background pattern. Compose into any styled
 * component that needs the striped fill (typically to mark a read-only or
 * "ghost" state). Caller owns layout and clipping.
 */
export const stripedBackground = ({
  baseColor,
  stripeWidth = 8,
  stripeOpacity = 0.06,
}: StripedBackgroundOptions): FlattenSimpleInterpolation => css`
  background-color: ${baseColor};
  background-image: repeating-linear-gradient(
    135deg,
    transparent 0 ${stripeWidth}px,
    rgba(0, 0, 0, ${stripeOpacity}) ${stripeWidth}px ${stripeWidth * 2}px
  );
`;

/**
 * Drop-in striped surface for callers that just need a wrapped element with
 * the hatched fill. Use the `stripedBackground` helper directly when the
 * stripe is conditional or you want to merge it into existing styles.
 */
export const StripedSurface = ({
  baseColor,
  children,
  className,
}: Props): JSX.Element => (
  <Surface $baseColor={baseColor} className={className}>
    {children}
  </Surface>
);

const Surface = styled.div<{ $baseColor: string }>`
  ${(props) => stripedBackground({ baseColor: props.$baseColor })}
`;
